/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ApiConfig} from "../../api";
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {AdnLogService} from '../../../util/adn-log.service';
import {AdnHttpService} from '../../../util/adn-http.service';
import {AdnHttpSender, createAdnHttpResourceSender} from '../../../util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';



@Injectable()
export class DataViewResource {
  private readonly uri: string = ApiConfig.obtain().getVersionedUri('dataview');

  currencies: any;
  dataViews: any;
  currentCalls: any;
  private readonly send: AdnHttpSender;

  constructor(
    private readonly adnLog: AdnLogService,
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService
  ) {
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  }

  query() {
    // not really useful from a UI perspective
    return this.send({method: 'GET', url: this.uri}).catch((response) => {
      this.adnLog.warn("DataView.query: " + response);
    });
  }

  getView(options, viewModel, parameters) {
    parameters = this.normalizeParameters(parameters);
    return this.send({
      method: 'POST',
      url: this.uri,
      body: [{
        viewModel: viewModel,
        parameters: parameters
      }],
      params: options
    }).then((data) => {
      if (!(data.results && data.results.length === 1)) {
        return Promise.reject('Expected only one data-view result');
      }
      const result: any = _.head(data.results);
      if (result.viewModel !== viewModel) {
        return Promise.reject('Expected viewModel ' + viewModel + ' but got ' + result.viewModel);
      }
      return result.dataView;
    }).catch((response) => {
      this.adnLog.warn("DataView.getView", response);
      return Promise.reject('Error returned from DataView');
    });
  }

  getFromCache(id) {
    if (!id) {
      return Promise.reject('DataView.getFromCache requires an id');
    }
    if (this.currentCalls && this.currentCalls[id]) {
      return this.currentCalls[id];
    }
    this.dataViews = this.dataViews || [];
    const liIndex = _.findIndex(this.dataViews, (dv) => {
      return _.get(dv, ['lineItem', 'id']) === id;
    });
    return liIndex > -1 ? Promise.resolve(this.dataViews[liIndex]) : this.get('LineItemView', {id: id}, false);
  }

  get(viewModel, parameters, includeStats) {
    parameters = this.normalizeParameters(parameters);
    if (viewModel === 'LineItemView' && !parameters.id) {
      this.adnLog.warn("DataView.get LineItemView called without parameters.id", parameters);
      return Promise.reject('DataView.get LineItemView requires parameters.id');
    }
    this.currentCalls = this.currentCalls || {};
    this.dataViews = this.dataViews || [];
    const viewModelPromise = this.send({
      method: 'POST',
      url: this.uri,
      body: [{
        viewModel: viewModel,
        parameters: parameters
      }],
      params: {'includeStats': includeStats}
    }).then((data) => {
      if (_.get(data, ['results', 'length']) !== 1) {
        return Promise.reject('Expected only one data-view result');
      }
      const result: any = _.head(data.results);
      if (result.viewModel !== viewModel) {
        return Promise.reject('Expected viewModel ' + viewModel + ' but got ' + result.viewModel);
      }
      const results = _.get(result, ['dataView', 'results']) || _.get(result, ['dataView']) || {};
      if (viewModel === 'LineItemView' && results.lineItem) {
        this.dataViews = _.filter(this.dataViews, function (dv) {
          return _.get(dv, ['lineItem', 'id']) !== results.lineItem.id;
        });
        let limitedResult = _.cloneDeep(results);
        limitedResult = _.pick(limitedResult, ['advertiser', 'lineItem', 'creatives', 'order']);
        if (limitedResult.lineItem && limitedResult.lineItem.id) {
          limitedResult.lineItem = _.pick(limitedResult.lineItem, ['id', 'name']);
        }
        if (limitedResult.advertiser && limitedResult.advertiser.id) {
          limitedResult.advertiser = _.pick(limitedResult.advertiser, ['id', 'name', 'advertiserUrl']);
        }
        if (limitedResult.order && limitedResult.order.id) {
          limitedResult.order = _.pick(limitedResult.order, ['id', 'name']);
        }

        limitedResult.creatives = _.map(limitedResult.creatives, function (c) {
          const creativeData = _.pick(c, ['id', 'name', 'width', 'height', 'layoutParameters', 'constraintsToUrls', 'layout.id', 'layout.name', 'layout.detectedCategory', 'objectState']);
          creativeData['url'] = _.map(creativeData['constraintsToUrls'] || [], function (v) {
            return v;
          })[0] || "";
          if (!creativeData['url'] && c['assetHtmlUrls']) {
            creativeData['url'] = _.map(c['assetHtmlUrls'] || [], function (kv) {
              return _.map(kv || [], function (v) {
                return v;
              })[0] || "";
            })[0] || "";
          }
          return creativeData;
        });
        this.dataViews.splice(0, this.dataViews.length - 50);
        this.dataViews.push(limitedResult);
      }
      return results;
    }).catch((response) => {
      this.adnLog.warn("DataView.get", response);
      return Promise.reject('Error returned from DataView');
    }).finally(() => {
      if (parameters.id) {
        this.currentCalls[parameters.id] = false;
      }
    });

    if (viewModel === 'LineItemView' && parameters.id) {
      this.currentCalls[parameters.id] = viewModelPromise;
    }

    return viewModelPromise;
  }

  private normalizeParameters(parameters: any): Record<string, any> {
    if (parameters && !_.isPlainObject(parameters)) {
      parameters = {id: parameters};
    }
    parameters = parameters || {};
    // JSON.stringify drops keys whose value is undefined, which yields parameters: {}
    if (_.isUndefined(parameters.id) && _.has(parameters, 'id')) {
      delete parameters.id;
    }
    return parameters;
  }
}
